import React, { useState } from "react";

/**
 * InputFormCard — Renders inline credential input fields inside the chat.
 *
 * Props:
 *   platform: string (e.g., "Snowflake")
 *   prompt: string (e.g., "Please provide your Snowflake credentials:")
 *   fields: [{ key, label, type, hint, sensitive }]
 *   onSubmit: (values: Record<string, string>) => void
 *   disabled: boolean
 */
const InputFormCard = ({ platform, prompt, fields = [], onSubmit, disabled = false }) => {
  const [values, setValues] = useState({});
  const [showSensitive, setShowSensitive] = useState({});

  if (!fields || fields.length === 0) return null;

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSensitive = (key) => {
    setShowSensitive((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      // Build a natural language message from the form values
      // so the agent can parse it the normal way
      const parts = Object.entries(values)
        .filter(([, v]) => v && v.trim())
        .map(([k, v]) => {
          const field = fields.find((f) => f.key === k);
          return `${field ? field.label : k}: ${v.trim()}`;
        });
      if (parts.length > 0) {
        onSubmit(parts.join("\n"));
      }
    }
  };

  const allFilled = fields.every((f) => values[f.key] && values[f.key].trim());

  return (
    <div className="w-full my-3 rounded-xl border border-blue-200 bg-blue-50/30 overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
        <span className="text-lg">🔑</span>
        <div>
          <p className="text-[0.7rem] font-black text-blue-500 uppercase tracking-[0.12em]">
            {platform} Credentials
          </p>
          <p className="text-[0.8rem] text-gray-600 font-medium">{prompt}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="block text-[0.75rem] font-bold text-gray-700 mb-1">
              {field.label}
            </label>
            <div className="relative">
              <input
                type={field.sensitive && !showSensitive[field.key] ? "password" : "text"}
                value={values[field.key] || ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                disabled={disabled}
                autoComplete="off"
                className="w-full px-3 py-2 text-[0.85rem] font-medium border border-gray-200 rounded-lg bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all disabled:opacity-50 disabled:bg-gray-50"
              />
              {field.sensitive && (
                <button
                  type="button"
                  onClick={() => toggleSensitive(field.key)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                >
                  {showSensitive[field.key] ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          type="submit"
          disabled={!allFilled || disabled}
          className={`w-full py-2.5 rounded-lg text-[0.85rem] font-bold transition-all ${
            allFilled && !disabled
              ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md active:scale-[0.98]"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Submit Credentials
        </button>
      </form>
    </div>
  );
};

export default InputFormCard;
